import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Hash, Save, RefreshCw, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultFormats = {
  training: { format: 'TRN-{YYYY}-{NNN}', next_number: 1, description: i18n.t('auto.lbl.trainingRecords') },
  document: { format: '{CAT}-{YYYY}-{NNN}', next_number: 1, description: i18n.t('auto.lbl.isoDocuments') },
  audit: { format: 'AUD-{YYYY}-{NNN}', next_number: 1, description: i18n.t('auto.lbl.auditRecords') },
  ncr: { format: 'NCR-{YYYY}-{NNN}', next_number: 1, description: i18n.t('auto.lbl.nonConformanceReports') },
  capa: { format: 'CAPA-{YYYY}-{NNN}', next_number: 1, description: i18n.t('auto.lbl.capaRecords') },
  invoice: { format: '{YYYY}/{MM}/{NNN}', next_number: 1, reset_monthly: true, description: i18n.t('auto.lbl.invoiceNumbering') },
  employee_invoice: { format: 'EMP-{YYYY}/{MM}/{NNN}', next_number: 1, reset_monthly: true, description: i18n.t('auto.lbl.employeeInvoiceNumbering') },
  checklist: { format: 'CL-{YYYY}-{NNN}', next_number: 1, description: i18n.t('auto.lbl.installationChecklistsReports') }
};

const placeholderHelp = [
  { code: '{YYYY}', description: 'Full year (e.g., 2026)' },
  { code: '{YY}', description: '2-digit year (e.g., 26)' },
  { code: '{MM}', description: '2-digit month (e.g., 01)' },
  { code: '{NNN}', description: '3-digit sequence (e.g., 001)' },
  { code: '{NNNN}', description: '4-digit sequence (e.g., 0001)' },
  { code: '{N}', description: i18n.t('auto.lbl.sequenceWithoutPadding') },
  { code: '{CAT}', description: i18n.t('auto.lbl.categoryCodeDocumentsOnly') }
];

const NumberingFormatsSettings = () => {
  const { t } = useTranslation();
  const [formats, setFormats] = useState(defaultFormats);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previews, setPreviews] = useState({});

  useEffect(() => {
    fetchFormats();
  }, []);

  useEffect(() => {
    // Generate previews for all formats
    generatePreviews();
  }, [formats]);

  const fetchFormats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const serverFormats = response.data.numbering_formats || {};
      // Merge with defaults to ensure all entity types exist
      const mergedFormats = { ...defaultFormats };
      Object.keys(serverFormats).forEach(key => {
        mergedFormats[key] = { ...defaultFormats[key], ...serverFormats[key] };
      });
      
      setFormats(mergedFormats);
    } catch (error) {
      console.error('Error fetching formats:', error);
      toast.error(t('auto.misc2.failedToLoadNumberingFormats'));
    } finally {
      setLoading(false);
    }
  };

  const generatePreviews = async () => {
    const token = localStorage.getItem('token');
    const newPreviews = {};
    
    for (const entityType of Object.keys(formats)) {
      try {
        const response = await axios.get(
          `${API}/settings/next-number/${entityType}?category=procedure`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        newPreviews[entityType] = response.data.next_number;
      } catch (error) {
        newPreviews[entityType] = 'Error';
      }
    }
    
    setPreviews(newPreviews);
  };

  const handleFormatChange = (entityType, field, value) => {
    setFormats(prev => ({
      ...prev,
      [entityType]: {
        ...prev[entityType],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/numbering-formats`,
        formats,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('auto.misc2.numberingFormatsSavedSuccessfully'));
      generatePreviews();
    } catch (error) {
      console.error('Error saving formats:', error);
      toast.error(error.response?.data?.detail || 'Failed to save numbering formats');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm(t('auto.misc2.resetAllNumberingFormatsToDefaults'))) {
      const resetFormats = {};
      Object.keys(formats).forEach(key => {
        resetFormats[key] = {
          ...defaultFormats[key],
          next_number: formats[key].next_number // Preserve sequence numbers
        };
      });
      setFormats(resetFormats);
    }
  };

  if (loading) {
    return <div className="p-4 text-center">{t('auto.misc2.loadingNumberingFormats')}</div>;
  }

  const entityLabels = {
    training: 'Training Records',
    document: 'ISO Documents',
    audit: 'Audit Records',
    ncr: 'Non-Conformance Reports',
    capa: 'CAPA Records',
    invoice: 'Invoices',
    employee_invoice: 'Employee Invoices',
    checklist: 'Installation Checklists & Reports'
  };

  return (
    <div className="space-y-6">
      {/* Help Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
            <Info className="h-5 w-5" />
            {t('auto.misc2.formatPlaceholders')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {placeholderHelp.map(({ code, description }) => (
              <div key={code} className="flex items-center gap-2">
                <code className="bg-blue-100 px-2 py-1 rounded text-sm font-mono text-blue-800">
                  {code}
                </code>
                <span className="text-sm text-blue-700">{description}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5 text-indigo-600" />
                {t('auto.misc2.automaticNumberingFormats')}
              </CardTitle>
              <CardDescription>
                {t('auto.misc2.configureAutoNumberingFormatsForTraining')}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button variant="outline" onClick={resetToDefaults} size="sm" data-testid="btn-resettodefaults">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('auto.misc2.resetToDefaults')}
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-numbering-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('auto.misc2.saving') : t('auto.misc2.saveChanges')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.keys(formats).map((entityType) => (
              <div 
                key={entityType} 
                className="p-4 bg-gray-50 rounded-lg space-y-3"
                data-testid={`numbering-${entityType}`}
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-medium text-gray-900">{entityLabels[entityType]}</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{t('auto.misc2.next')}</span>
                    <Badge variant="outline" className="font-mono">
                      {previews[entityType] || 'Loading...'}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Label>{t('auto.misc2.formatPattern')}</Label>
                    <Input
                      value={formats[entityType].format}
                      onChange={(e) => handleFormatChange(entityType, 'format', e.target.value)}
                      className="font-mono"
                      placeholder="e.g., TRN-{YYYY}-{NNN}"
                      data-testid={`format-${entityType}`}
                    />
                  </div>
                  <div>
                    <Label>{t('auto.misc2.nextSequence')}</Label>
                    <Input
                      type="number"
                      value={formats[entityType].next_number}
                      onChange={(e) => handleFormatChange(entityType, 'next_number', parseInt(e.target.value) || 1)}
                      min="1"
                      data-testid={`next-number-${entityType}`}
                    />
                  </div>
                </div>
                
                {entityType === 'document' && (
                  <p className="text-xs text-gray-500">
                    Category codes: QM (Quality Manual), PR (Procedure), WI (Work Instruction), 
                    FM (Form), PO (Policy), SP (Specification), RC (Record), EX (External), TM (Template)
                  </p>
                )}
                {(entityType === 'invoice' || entityType === 'employee_invoice') && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`reset-monthly-${entityType}`}
                      checked={formats[entityType].reset_monthly ?? true}
                      onChange={(e) => handleFormatChange(entityType, 'reset_monthly', e.target.checked)}
                      className="rounded"
                      data-testid={`${entityType}-reset-monthly`}
                    />
                    <label htmlFor={`reset-monthly-${entityType}`} className="text-sm text-gray-600">
                      {t('auto.misc2.resetSequenceNumberEachMonth')}
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NumberingFormatsSettings;
